import { useEffect, useState } from 'react'

export interface FocusClockInput {
  /** Beginn des laufenden Abschnitts; `null` heißt: es läuft gerade keiner. */
  startTime: Date | null
  /** Schon gebuchte Sekunden derselben Aufgabe (beendete Abschnitte). */
  bookedSeconds?: number
  /** Veranschlagte Dauer in Sekunden; ohne sie zählt die Uhr hoch. */
  targetSeconds?: number | null
}

export interface FocusClock {
  /** Gesamte Fokuszeit der Aufgabe: gebucht plus laufend. */
  seconds: number
  /** Sekunden seit dem letzten Play; `0`, wenn nichts läuft. */
  sectionSeconds: number
  /** Rest der Dauer; `null` ohne Dauer, negativ in der Überzeit. */
  remaining: number | null
  /** `true`, sobald die Dauer aufgebraucht ist. Still — es meldet sich nichts. */
  overtime: boolean
}

/**
 * **Die einzige tickende Stelle der Kernschleife.**
 *
 * Rechnet jede Sekunde `jetzt − startTime` neu und zählt die gebuchten Abschnitte
 * dazu. **Kein Zähler, der hochgezählt wird:** Nach einem Zuklappen, einem
 * gedrosselten Fenster oder einem verschluckten Intervall stünde der falsch — die
 * Uhr fragt statt dessen immer die Systemzeit, so wie die Anzeige in ADR 0025
 * beschrieben ist.
 *
 * Läuft nichts (`startTime: null`), tickt auch nichts; die Uhr zeigt dann die
 * gebuchte Zeit und steht still.
 */
export function useFocusClock({ startTime, bookedSeconds = 0, targetSeconds }: FocusClockInput): FocusClock {
  const startedAt = startTime ? startTime.getTime() : null
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    if (startedAt === null) return
    setNow(Date.now())
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [startedAt])

  // Eine krumme Uhr (oder ein nachgereichter Zeitpunkt) darf die Anzeige nicht
  // rückwärts laufen lassen.
  const running = startedAt === null ? 0 : Math.max(0, Math.floor((now - startedAt) / 1000))
  const seconds = bookedSeconds + running
  const remaining = targetSeconds == null ? null : targetSeconds - seconds

  return {
    seconds,
    sectionSeconds: running,
    remaining,
    overtime: remaining !== null && remaining < 0,
  }
}
